import React, { useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useAdminLanguage } from "../../../context/AdminLanguageContext";
import WordWheelPuzzleStatsPanel from "./WordWheelPuzzleStatsPanel";
import WordWheelPromotionPanel from "./WordWheelPromotionPanel";
import WordWheelTopUsersPanel from "./WordWheelTopUsersPanel";
import WordWheelUserStatsPanel from "./WordWheelUserStatsPanel";

const TAB_USERS = 0;
const TAB_TOP_USERS = 1;
const TAB_PUZZLES = 2;
const TAB_PROMOTION = 3;
const VALID_TABS = [TAB_USERS, TAB_TOP_USERS, TAB_PUZZLES, TAB_PROMOTION];
const TAB_STORAGE_KEY = "admin.word_wheel.stats.tab";

function parseTab(value) {
    const v = parseInt(value, 10);
    return VALID_TABS.includes(v) ? v : null;
}

function loadStoredTab() {
    try {
        const fromQuery = parseTab(new URLSearchParams(window.location.search).get("tab"));
        if (fromQuery != null) return fromQuery;
        const v = parseTab(window.localStorage.getItem(TAB_STORAGE_KEY));
        if (v != null) return v;
    } catch {}
    return TAB_USERS;
}

export default function AdminWordWheelStatsMain() {
    const { language } = useAdminLanguage();
    const [activeTab, setActiveTab] = useState(loadStoredTab);
    // A panel loads its stats on mount, so mount it on first visit and keep it mounted after that.
    const [visitedTabs, setVisitedTabs] = useState(() => [loadStoredTab()]);

    const handleTabChange = (event, value) => {
        setActiveTab(value);
        setVisitedTabs((tabs) => (tabs.includes(value) ? tabs : [...tabs, value]));
        try {
            window.localStorage.setItem(TAB_STORAGE_KEY, String(value));
        } catch {}
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}>
                Word Wheel Statistics
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="Word wheel statistics sections"
                >
                    <Tab
                        label="Users"
                        id="ww-stats-tab-0"
                        aria-controls="ww-stats-tabpanel-0"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        label="Top users"
                        id="ww-stats-tab-1"
                        aria-controls="ww-stats-tabpanel-1"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        label="Puzzles"
                        id="ww-stats-tab-2"
                        aria-controls="ww-stats-tabpanel-2"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        label="Promotion"
                        id="ww-stats-tab-3"
                        aria-controls="ww-stats-tabpanel-3"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                </Tabs>
            </Box>

            {/* Panels stay mounted while hidden so switching tabs does not reload their stats */}
            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_USERS}
                id="ww-stats-tabpanel-0"
                aria-labelledby="ww-stats-tab-0"
            >
                {visitedTabs.includes(TAB_USERS) && <WordWheelUserStatsPanel language={language} />}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_TOP_USERS}
                id="ww-stats-tabpanel-1"
                aria-labelledby="ww-stats-tab-1"
            >
                {visitedTabs.includes(TAB_TOP_USERS) && <WordWheelTopUsersPanel language={language} />}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_PUZZLES}
                id="ww-stats-tabpanel-2"
                aria-labelledby="ww-stats-tab-2"
            >
                {visitedTabs.includes(TAB_PUZZLES) && <WordWheelPuzzleStatsPanel language={language} />}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_PROMOTION}
                id="ww-stats-tabpanel-3"
                aria-labelledby="ww-stats-tab-3"
            >
                {visitedTabs.includes(TAB_PROMOTION) && <WordWheelPromotionPanel language={language} />}
            </Box>
        </Box>
    );
}
